/**
 * Region Gate Core JavaScript Logic
 * 
 * Byte-identical and store-independent. All store configurations are read
 * from the DOM structure generated by Shopify Liquid.
 */
(function() {
  const STORAGE_KEY = 'rg:state';
  const EXPIRY_KEY = 'rg:state:expires';
  const COOKIE_NAME = 'rg_state';
  const DISMISSED_SESSION_KEY = 'rg:dismissed';

  let originalActiveElement = null;

  // Active query parameter used to filter collections
  const ACTIVE_FILTER_PARAM = 'filter.v.m.custom.allowed_states'; 

  // Immediate Check to hide product grid container and prevent flash on first paint
  (function() {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; rg_state=`);
    const savedStateCode = parts.length === 2 ? decodeURIComponent(parts.pop().split(';').shift()) : null;
    
    if (savedStateCode) {
      const isCollection = window.location.pathname.includes('/collections/');
      const isSearch = window.location.pathname.includes('/search');
      const urlParams = new URLSearchParams(window.location.search);
      const currentFilter = urlParams.get('filter.p.tag') || urlParams.get('filter.v.m.custom.allowed_states');
      
      // If we are on collection/search and the current URL does not have the saved state filter
      if ((isCollection || isSearch) && currentFilter !== savedStateCode) {
        const style = document.createElement('style');
        style.id = 'rg-initial-loading-style';
        style.textContent = `
          #ProductGridContainer { 
            opacity: 0 !important; 
            transition: opacity 0.25s ease-in-out;
          }
        `;
        document.documentElement.appendChild(style);
      }
    }
  })();

  // Global History replacement wrapper to strip allowed states from browser URL bar
  const originalReplaceState = history.replaceState;
  history.replaceState = function(state, title, url) {
    if (url) {
      try {
        const urlObj = new URL(url, window.location.origin);
        ['filter.p.tag', 'filter.v.m.custom.allowed_states'].forEach(param => {
          if (urlObj.searchParams.has(param)) {
            urlObj.searchParams.delete(param);
          }
        });
        url = urlObj.toString();
      } catch (e) {}
    }
    return originalReplaceState.apply(this, [state, title, url]);
  };

  const originalPushState = history.pushState;
  history.pushState = function(state, title, url) {
    if (url) {
      try {
        const urlObj = new URL(url, window.location.origin);
        ['filter.p.tag', 'filter.v.m.custom.allowed_states'].forEach(param => {
          if (urlObj.searchParams.has(param)) {
            urlObj.searchParams.delete(param);
          }
        });
        url = urlObj.toString();
      } catch (e) {}
    }
    return originalPushState.apply(this, [state, title, url]);
  };

  // Global Fetch wrapper to automatically inject shipping state parameter
  // const originalFetch = window.fetch;
  // window.fetch = function(input, init) {
  //   let urlString = typeof input === 'string' ? input : (input instanceof URL ? input.href : (input && input.url ? input.url : ''));
  //   const isCollectionOrSearch = urlString.includes('/collections/') || urlString.includes('/search');

  //   if (isCollectionOrSearch && window.RegionGate) {
  //     const saved = window.RegionGate.get();
  //     if (saved) {
  //       try {
  //         const urlObj = new URL(urlString, window.location.origin);
  //         const hasTagFilter = urlObj.searchParams.has('filter.p.tag');
  //         const hasMetafieldFilter = urlObj.searchParams.has('filter.v.m.custom.allowed_states');

  //         if (!hasTagFilter && !hasMetafieldFilter) {
  //           urlObj.searchParams.set(ACTIVE_FILTER_PARAM, saved.label);
  //           if (typeof input === 'string') {
  //             input = urlObj.toString();
  //           } else if (input instanceof URL) {
  //             input = urlObj;
  //           } else if (input && input.url) {
  //             input = new Request(urlObj.toString(), input);
  //           }
  //         }
  //       } catch (e) {
  //         console.warn('RegionGate: Fetch parameter injection failed', e);
  //       }
  //     }
  //   }
  //   return originalFetch.apply(this, [input, init]);
  // };

  // Utility: Safe Storage Check
  function isStorageSupported(storageType) {
    try {
      const storage = window[storageType];
      const x = '__storage_test__';
      storage.setItem(x, x);
      storage.removeItem(x);
      return true;
    } catch (e) {
      return false;
    }
  }

  const hasLocalStorage = isStorageSupported('localStorage');
  const hasSessionStorage = isStorageSupported('sessionStorage');

  // Utility: Cookie Helpers
  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return decodeURIComponent(parts.pop().split(';').shift());
    return null;
  }

  function setCookie(name, value, days) {
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = `; expires=${date.toUTCString()}`;
    }
    document.cookie = `${name}=${encodeURIComponent(value || '')}${expires}; path=/; SameSite=Lax`;
  }

  function deleteCookie(name) {
    document.cookie = `${name}=; Max-Age=-99999999; path=/; SameSite=Lax`;
  }

  // Utility: Browser/Bot detection
  function isBot() {
    if (navigator.webdriver) return true;
    const botUserAgents = [
      'googlebot',
      'bingbot',
      'yandexbot',
      'ahrefsbot',
      'baiduspider',
      'facebookexternalhit',
      'twitterbot',
      'rogue',
      'markdownbot'
    ];
    const ua = navigator.userAgent.toLowerCase();
    return botUserAgents.some(bot => ua.indexOf(bot) > -1);
  }

  // Utility: Detect Shopify Theme Editor (Customizer)
  function isThemeEditorActive() {
    return !!(window.Shopify && window.Shopify.designMode === true);
  }

  // Core API Class
  const RegionGateAPI = {
    // Get currently saved region
    get() {
      try {
        // Expiry check first
        if (hasLocalStorage) {
          const expiry = localStorage.getItem(EXPIRY_KEY);
          if (expiry && Date.now() > parseInt(expiry, 10)) {
            this.clear();
            return null;
          }
        }

        // Read state code from localStorage or Cookie
        let code = null;
        if (hasLocalStorage) {
          code = localStorage.getItem(STORAGE_KEY);
        }
        if (!code) {
          code = getCookie(COOKIE_NAME);
        }

        if (!code) return null;

        // Find label
        const regionsList = this.regions();
        const matched = regionsList.find(r => r.code.toUpperCase() === code.toUpperCase());
        return {
          code: matched ? matched.code : code,
          label: matched ? matched.label : code
        };
      } catch (err) {
        console.warn('RegionGate: Failed to read storage', err);
        return null;
      }
    },

    // Set region with code
    set(code) {
      return new Promise((resolve) => {
        try {
          const upperCode = code.toUpperCase();
          const matchedRegion = this.regions().find(r => r.code.toUpperCase() === upperCode);
          if (!matchedRegion) {
            console.error(`RegionGate: Region code "${code}" not defined in configuration.`);
            resolve(false);
            return;
          }

          const previousState = this.get();
          const previousCode = previousState ? previousState.code : null;

          // 1. Save to LocalStorage (with expiry)
          const wrapper = document.getElementById('rg-popup-wrapper');
          const expiryDays = wrapper ? parseFloat(wrapper.dataset.expiryDays) || 30 : 30;

          const savedValue = matchedRegion.code;

          if (hasLocalStorage) {
            localStorage.setItem(STORAGE_KEY, savedValue);
            localStorage.setItem(EXPIRY_KEY, (Date.now() + (expiryDays * 24 * 60 * 60 * 1000)).toString());
          }

          // 2. Save to Cookie
          setCookie(COOKIE_NAME, savedValue, expiryDays);

          // 3. Save to Shopify Cart Attribute (Liquid synchronization)
          const rootUrl = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
          const updateUrl = `${rootUrl.replace(/\/$/, '')}/cart/update.js`;

          console.log(updateUrl,savedValue)
          fetch(updateUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { region_gate_state: savedValue } })
          })
          .then(() => {
            // Success 
            
            resolve(true);
          })
          .catch((err) => {
            console.warn('RegionGate: Cart attribute update failed but local state is set', err);
            resolve(true); // Don't block UI progression if network fails
          });

          // Re-update select element values if popup is open
          const selectEl = document.querySelector('[data-rg-select]');
          if (selectEl) {
            selectEl.value = savedValue;
            const applyBtn = document.querySelector('[data-rg-apply]');
            if (applyBtn) applyBtn.removeAttribute('disabled');
          }

          // Dispatch Event
          document.dispatchEvent(new CustomEvent('region-gate:change', {
            detail: {
              code: savedValue,
              label: matchedRegion.label,
              previous: previousCode
            }
          }));

          // Live region announcement
          const announcer = document.getElementById('rg-announcer');
          if (announcer) {
            announcer.textContent = `Shipping region updated to ${matchedRegion.label}.`;
          }

          

        } catch (err) {
          console.warn('RegionGate: Failed to set region', err);
          resolve(false);
        }
      });
    },

    // Clear saved region
    clear() {
      return new Promise((resolve) => {
        try {
          if (hasLocalStorage) {
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(EXPIRY_KEY);
          }
          deleteCookie(COOKIE_NAME);

          // Remove Shopify Cart Attribute
          const rootUrl = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
          const updateUrl = `${rootUrl.replace(/\/$/, '')}/cart/update.js`;

          fetch(updateUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { region_gate_state: '' } })
          })
          .then(() => resolve(true))
          .catch(() => resolve(true));

          // Reset UI
          const selectEl = document.querySelector('[data-rg-select]');
          if (selectEl) {
            selectEl.value = '';
            const applyBtn = document.querySelector('[data-rg-apply]');
            if (applyBtn) applyBtn.setAttribute('disabled', 'true');
          }

          document.dispatchEvent(new CustomEvent('region-gate:clear'));
        } catch (err) {
          console.warn('RegionGate: Failed to clear region', err);
          resolve(false);
        }
      });
    },

    // Open popup
    open() { 
      const wrapper = document.getElementById('rg-popup-wrapper');
      if (!wrapper) return;
      console.log('open called')
      originalActiveElement = document.activeElement;
      wrapper.style.display = 'flex';
      
      // Accessibility: Scroll Lock & Inert
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
      
      // Make background content inert or hidden
      document.querySelectorAll('body > :not(#rg-popup-wrapper)').forEach(el => {
        el.setAttribute('aria-hidden', 'true');
      });

      // Focus on the select element inside modal
      const selectEl = wrapper.querySelector('[data-rg-select]');
      if (selectEl) {
        selectEl.focus();
      }

      document.dispatchEvent(new CustomEvent('region-gate:open'));
    },

    // Close popup
    close() {
      const wrapper = document.getElementById('rg-popup-wrapper');
      if (!wrapper) return;

      wrapper.style.display = 'none';

      // Restore scroll and padding
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';

      // Remove background hidden attribute
      document.querySelectorAll('body > :not(#rg-popup-wrapper)').forEach(el => {
        el.removeAttribute('aria-hidden');
      });

      // Restore active element focus
      if (originalActiveElement && typeof originalActiveElement.focus === 'function') {
        originalActiveElement.focus();
      }

      document.dispatchEvent(new CustomEvent('region-gate:close'));
    },

    // Check if popup is currently open
    isOpen() {
      const wrapper = document.getElementById('rg-popup-wrapper');
      return wrapper ? wrapper.style.display !== 'none' : false;
    },

    // List all regions configured in select markup
    regions() {
      const selectEl = document.querySelector('[data-rg-select]');
      if (!selectEl) return [];

      const options = Array.from(selectEl.options);
      return options
        .filter(opt => opt.value !== '')
        .map(opt => ({
          code: opt.value.toUpperCase(),
          label: opt.textContent.trim()
        }));
    },

    // Check availability of list of restricted states
    availability(restrictedCodes) {
      const saved = this.get();
      if (!saved) return 'unknown';

      let list = restrictedCodes;
      if (typeof list === 'string') {
        try {
          list = JSON.parse(list);
        } catch (e) {
          // If it's a comma-separated list
          list = list.split(',').map(s => s.trim());
        }
      }

      if (!list || !Array.isArray(list) || list.length === 0) return 'available';

      const savedCode = saved.code.toUpperCase().trim();
      const isRestricted = list.some(code => code && typeof code === 'string' && code.toUpperCase().trim() === savedCode);

      return isRestricted ? 'unavailable' : 'available';
    },

    // Check allowed availability of a product based on its allowed states metafield list
    allowedStatesAvailability(allowedJson) {
      const saved = this.get();
      if (!saved) return 'unknown';

      let allowed = allowedJson;
      if (typeof allowed === 'string') {
        try {
          allowed = JSON.parse(allowed);
        } catch (e) {
          allowed = allowed.split(',').map(s => s.trim());
        }
      }

      if (!allowed || !Array.isArray(allowed) || allowed.length === 0) {
        // If it doesn't have any allowed states configured, it is available everywhere
        return 'available';
      }

      const upperAllowed = allowed.map(s => typeof s === 'string' ? s.toUpperCase().trim() : '');
      const savedCode = saved.code.toUpperCase().trim();
      const savedLabel = saved.label.toUpperCase().trim();

      const isAllowed = upperAllowed.some(s => s === savedCode || s === savedLabel);

      return isAllowed ? 'available' : 'unavailable';
    }
  };

  // Expose Globally
  window.RegionGate = RegionGateAPI;


 // --- Back/forward fix: reload if the state changed since this page's HTML was rendered ---
  (function() {
    const getCode = () => {
      const s = RegionGateAPI.get();
      return s ? s.code.toUpperCase() : '';
    };
    const recordKey = 'rg:rendered:' + location.pathname + location.search;
    const navEntry = performance.getEntriesByType('navigation')[0];
    const isBackForward = navEntry && navEntry.type === 'back_forward';
    const currentCode = getCode();

    // Case 1: page re-executed after Back (HTTP-cached HTML)
    if (isBackForward && hasSessionStorage) {
      const renderedWith = sessionStorage.getItem(recordKey);
      if (renderedWith !== null && renderedWith !== currentCode) {
        window.location.reload();
        return;
      }
    }

    // Fresh load: record the state this HTML was rendered with
    if (!isBackForward && hasSessionStorage) {
      sessionStorage.setItem(recordKey, currentCode);
    }

    // Case 2: page restored from bfcache (scripts do not re-run)
    window.addEventListener('pageshow', function(event) {
      if (!event.persisted) return;
      if (getCode() !== currentCode) {
        window.location.reload();
      }
    });
  })();

  // Helper to fetch filtered HTML via AJAX and replace DOM containers dynamically
  function updateCollectionGridAJAX(filterStateLabel) {
    const urlObj = new URL(window.location.href);
    if (filterStateLabel) {
      urlObj.searchParams.set(ACTIVE_FILTER_PARAM, filterStateLabel);
      urlObj.searchParams.delete(ACTIVE_FILTER_PARAM === 'filter.p.tag' ? 'filter.v.m.custom.allowed_states' : 'filter.p.tag');
    } else {
      urlObj.searchParams.delete('filter.p.tag');
      urlObj.searchParams.delete('filter.v.m.custom.allowed_states');
    }
    console.log(urlObj)
    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) {
      gridContainer.style.opacity = '0.5';
      gridContainer.style.pointerEvents = 'none';
      gridContainer.setAttribute('data-rg-loading', 'true');
    }
    const drawer = document.getElementById('FacetDrawer');
    if (drawer) drawer.classList.add('loading');

    fetch(urlObj.toString())
      .then(response => response.text())
      .then(responseText => {
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        
        // Containers to swap
        const ids = [
          'ProductGridContainer',
          'FacetFiltersActive',
          'ProductCount',
          'SortByContainer',
          'FacetFiltersContainer',
          'MobileFacetFiltersContainer'
        ];

        ids.forEach(id => {
          const dest = document.getElementById(id);
          const src = parsedHTML.getElementById(id);
          if (dest && src) {
            dest.innerHTML = src.innerHTML;
          }
        });

        // Trigger custom event so standard collection list/infinite-scroll event listeners reinitialize
        document.dispatchEvent(new CustomEvent('collection:reloaded')); 
        // Remove loading state and styling
        const initialLoadingStyle = document.getElementById('rg-initial-loading-style');
        if (initialLoadingStyle) {
          initialLoadingStyle.remove();
        }
        if (gridContainer) {
          gridContainer.style.opacity = '1';
          gridContainer.style.pointerEvents = '';
          gridContainer.removeAttribute('data-rg-loading');
        }
        if (drawer) drawer.classList.remove('loading');
      })
      .catch(err => {
        console.error('RegionGate: AJAX grid update failed', err);
        const initialLoadingStyle = document.getElementById('rg-initial-loading-style');
        if (initialLoadingStyle) {
          initialLoadingStyle.remove();
        }
        if (gridContainer) {
          gridContainer.style.opacity = '1';
          gridContainer.style.pointerEvents = '';
          gridContainer.removeAttribute('data-rg-loading');
        }
        if (drawer) drawer.classList.remove('loading');
      });
  }

  // Helper to reload/update page with collection filter
  function reloadWithFilter(stateName) {
    // const isCollection = window.location.pathname.includes('/collections/');
    // const isSearch = window.location.pathname.includes('/search');
    
    window.location.reload();

    // if (isCollection || isSearch) {
    //   const saved = RegionGateAPI.get();
    //   updateCollectionGridAJAX(saved ? saved.label : null);
    // } else {
    //   window.location.reload();
    // }
  }

  // Initialize UI Events on DOMContentLoaded
  function initRegionGate() {
    const wrapper = document.getElementById('rg-popup-wrapper');
    
    // AJAX-based collection/search page load filtering
    const savedRegion = RegionGateAPI.get();
    // const isCollection = window.location.pathname.includes('/collections/');
    // const isSearch = window.location.pathname.includes('/search');
    
    // if (isCollection || isSearch) {
    //   const urlParams = new URLSearchParams(window.location.search);
    //   const currentFilter = urlParams.get('filter.p.tag') || urlParams.get('filter.v.m.custom.allowed_states');
      
    //   if (savedRegion) {
    //     if (currentFilter === savedRegion.label) {
    //       // Clean the URL bar if it already has the parameter
    //       const cleanUrl = new URL(window.location.href);
    //       cleanUrl.searchParams.delete('filter.p.tag');
    //       cleanUrl.searchParams.delete('filter.v.m.custom.allowed_states');
    //       history.replaceState({}, '', cleanUrl.toString());
    //     } else {
    //       // Fetch filtered products dynamically
    //       updateCollectionGridAJAX(savedRegion.label);
    //     }
    //   } else {
    //     // No region saved. Clean filter if present
    //     if (currentFilter) {
    //       updateCollectionGridAJAX(null);
    //     }
    //   }
    // }
    
    // 0. Initialize Header Selectors & Dropdown
    // Header trigger: toggle dropdown, NOT popup modal
    const headerWrap = document.querySelector('[data-rg-header-wrap]');
    const headerTrigger = headerWrap ? headerWrap.querySelector('[data-rg-open]') : null;
    const headerDropdown = headerWrap ? headerWrap.querySelector('[data-rg-dropdown-menu]') : null;

    if (headerTrigger && headerDropdown) {
      headerTrigger.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        headerDropdown.classList.toggle('is-open');
      });

      // Close dropdown when clicking outside
      document.addEventListener('click', function(e) {
        if (!headerWrap.contains(e.target)) {
          headerDropdown.classList.remove('is-open');
        }
      });
    }

    // Other open buttons (NOT in header) still open the popup modal
    document.querySelectorAll('[data-rg-open]').forEach(btn => {
      if (btn === headerTrigger) return; // skip header trigger
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        RegionGateAPI.open();
      });
    });

    // State selection click — direct binding on all state buttons
    document.querySelectorAll('[data-rg-state-select]').forEach(function(stateBtn) {
      stateBtn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        var stateCode = stateBtn.dataset.rgStateSelect;
        localStorage.setItem('selectCode', stateCode);
        // Close dropdown immediately
        // if (headerDropdown) headerDropdown.classList.remove('is-open');
        // RegionGateAPI.set(stateCode).then(function() {
        //   reloadWithFilter(stateCode);
        // });
      });
    });

    // header confirm button
    const applyButtons = document.querySelectorAll('.rg-btn-header-apply');
    applyButtons.forEach((btn) => {
      btn.addEventListener('click', (event) => {
        btn.classList.add('is-loading');
  
        const headerDropdown = btn.closest('.rg-header-dropdown');

        const selectState = localStorage.getItem('selectCode');

        // Close dropdown immediately
        if (headerDropdown) {
          headerDropdown.classList.remove('is-open');
        }

        RegionGateAPI.set(selectState).then(function(result) {
          reloadWithFilter(selectState);
          localStorage.removeItem('selectCode');
          if (result) {
            window.location.reload();
          }
        });
      });
    });

    // Reactively update header labels on events
    document.addEventListener('region-gate:change', function(e) {
      document.querySelectorAll('[data-rg-label]').forEach(el => {
        el.textContent = ' Ship to: ' + e.detail.label;
      });

      const prompt = document.getElementById('rg-homepage-prompt');
      if (prompt) {
        prompt.style.opacity = '0';
        prompt.style.transform = 'translateY(-10px)';
        prompt.style.maxHeight = '0';
        prompt.style.marginTop = '0';
        prompt.style.marginBottom = '0';
        prompt.style.paddingTop = '0';
        prompt.style.paddingBottom = '0';
        setTimeout(() => prompt.remove(), 300);
      }
    });
    
    document.addEventListener('region-gate:clear', function() {
      document.querySelectorAll('[data-rg-label]').forEach(el => {
        el.textContent = 'Select State';
      });
    });

    if (!wrapper) return;

    const selectEl = wrapper.querySelector('[data-rg-select]');
    const applyBtn = wrapper.querySelector('[data-rg-apply]');
    const dismissBtn = wrapper.querySelector('[data-rg-dismiss]');
    const closeBtn = wrapper.querySelector('[data-rg-close]');
    const overlay = wrapper.querySelector('[data-rg-backdrop]');

    // 1. Select Dropdown Interaction
    if (selectEl) {
      selectEl.addEventListener('change', function() {
        if (selectEl.value !== '') {
          applyBtn.removeAttribute('disabled');
        } else {
          applyBtn.setAttribute('disabled', 'true');
        }
      });
    }

    // 1.1 Custom Searchable Selector Popup logic
    const customPopupBtns = wrapper.querySelectorAll('[data-rg-popup-select-btn]');
    const popupSearchInput = document.getElementById('rg-popup-search');
    const popupStatesList = document.getElementById('rg-popup-states-list');

    if (customPopupBtns.length > 0 ) {
      customPopupBtns.forEach(btn => {
        btn.addEventListener('click', function(e) {
          e.preventDefault();
          const val = btn.getAttribute('data-rg-popup-select-btn');
          localStorage.setItem('selectCode', val); 

          // Update active button state
          customPopupBtns.forEach(b => b.classList.remove('is-selected'));
          btn.classList.add('is-selected');
          popupSearchInput.value = btn.textContent.trim()
          popupStatesList.closest('#rg-popup-state-box').style.display = 'none'
        });
      });
    }

    if (popupSearchInput && popupStatesList) {
      popupSearchInput.addEventListener('input', function() {
        const query = popupSearchInput.value.toLowerCase().trim();
        const items = popupStatesList.querySelectorAll('.rg-custom-select-item');
        
        items.forEach(item => {
          const name = item.getAttribute('data-name') || '';
          const value = item.getAttribute('data-value') || '';
          if (name.includes(query) || value.toLowerCase().includes(query)) {
            item.style.display = '';
          } else {
            item.style.display = 'none';
          }
        }); 

      });
    }

    // 1.2 Header Dropdown Search logic
    const headerSearchInputs = document.querySelectorAll('.rg-dropdown-search-input');

    headerSearchInputs.forEach((headerSearchInput) => { 
      const parentContainer = headerSearchInput.closest('.rg-header-dropdown') || document;
      const headerStatesList = parentContainer.querySelector('.rg-header-dropdown-list');
      const dropIcon = parentContainer.querySelector('.rg-dropdown-search-wrap .us-dropdown-icon')

      if (headerSearchInput && headerStatesList) { 
        headerSearchInput.addEventListener('keydown', function(e) {
          if (e.key === ' ' || e.keyCode === 32) {
            e.stopPropagation();
          }
        });

        // Handle real-time filtering
        headerSearchInput.addEventListener('input', function() {
          const query = headerSearchInput.value.toLowerCase().trim();
          const items = headerStatesList.querySelectorAll('.rg-dropdown-li');

          items.forEach((item) => {
            const name = (item.getAttribute('data-name') || '').toLowerCase();
            const value = (item.getAttribute('data-value') || '').toLowerCase();

            if (name.includes(query) || value.includes(query)) {
              item.style.display = '';
            } else {
              item.style.display = 'none';
            }
          });
        });

        // Show list on click
        headerSearchInput.addEventListener('click', () => {
          headerStatesList.style.display = 'block';
        });

        dropIcon.addEventListener('click', () => {
          headerStatesList.style.display = 'block';
        });
      }
    });

    let ageVerification = document.getElementById('age-confirm-checkbox-value');
    if(!ageVerification.checked && localStorage.getItem('ageVerified') == null ){
      document.querySelector('.age-confirm-checkbox').style.display = 'flex';
      document.querySelector('.dynamic-confirm').style.display = 'inline-block';
    } else {
      document.querySelector('.dynamic-confirm').style.display = 'none';
    }

    document.getElementById('enter-btn').addEventListener("click", (event) => {
      let btn = document.getElementById('enter-btn');
      btn.classList.add('is-loading');
      if(ageVerification.checked){
        localStorage.setItem('ageVerified', 'true'); 
        ageVerification.closest('.age-confirm-checkbox').style.borderColor = 'lightgray'
        if(localStorage.getItem('selectCode') !== null ){
          let selectState = localStorage.getItem('selectCode') ;
          
          // Close dropdown immediately
          if (headerDropdown) headerDropdown.classList.remove('is-open');
          RegionGateAPI.set(selectState).then(function(result) {
            window.location.reload();
          }); 
        } else{
          document.querySelector('#rg-popup-search').style.borderColor = 'red';
          btn.classList.remove('is-loading');
        } 
      }
      else if( localStorage.getItem('ageVerified') == 'true' ){
        console.log('herhere')
        let selectState = localStorage.getItem('selectCode') ;
        
        // RegionGateAPI.set(selectState).then(function(result) {
        //     window.location.reload();
        //   }); 

        if( !selectState ) { // new
          if (selectEl && selectEl.value !== '') {
            RegionGateAPI.set(selectEl.value).then(() => {
              RegionGateAPI.close();
              reloadWithFilter(selectEl.value); // Force page reload to apply changes globally
            });
          }
        } else {
          RegionGateAPI.set(selectState).then(function(result) {
            window.location.reload();
          }); 
        } // new end
      }
      else{
        ageVerification.closest('.age-confirm-checkbox').style.borderColor = 'red'
        if(!document.querySelector('#rg-popup-search').value) {
          document.querySelector('#rg-popup-search').style.borderColor = 'red'
        }else{
          document.querySelector('#rg-popup-search').style.borderColor = 'lightgray' 
        }
        btn.classList.remove('is-loading');
      }
    })
    // 2. Primary CTA: Apply
    if (applyBtn) {
      applyBtn.addEventListener('click', function() {
        if (selectEl && selectEl.value !== '') {
          RegionGateAPI.set(selectEl.value).then(() => {
            RegionGateAPI.close();
            reloadWithFilter(selectEl.value); // Force page reload to apply changes globally
          });
        }
      });
    }

    // Dismissal Handler
    function dismissPopup() {
      if (hasSessionStorage) {
        try {
          sessionStorage.setItem(DISMISSED_SESSION_KEY, 'true');
        } catch (e) {}
      }
      RegionGateAPI.close();
      document.dispatchEvent(new CustomEvent('region-gate:dismiss'));
    }

    // 3. Secondary CTA: Continue Browsing
    if (dismissBtn) {
      dismissBtn.addEventListener('click', dismissPopup);
    }

    // 4. Close Icon (X)
    if (closeBtn) {
      closeBtn.addEventListener('click', dismissPopup);
    }

    // 5. Backdrop Click (Overlay)
    const isBlocking = wrapper.dataset.blocking === 'true';
    if (overlay && !isBlocking) {
      overlay.addEventListener('click', dismissPopup);
    }

    // 6. Keyboard Accessibility (Escape + Focus Trap)
    wrapper.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && !isBlocking) {
        dismissPopup();
        return;
      }

      if (e.key === 'Tab') {
        const focusableElements = wrapper.querySelectorAll('select, button:not([disabled])');
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) { // Shift + Tab
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else { // Tab
          if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      }
    });

    // 7. Auto-trigger Evaluation (savedRegion is declared at the top of initRegionGate)
    const hasSavedRegion = !!savedRegion;
    const isDismissed = hasSessionStorage && sessionStorage.getItem(DISMISSED_SESSION_KEY) === 'true';
    const isSkipUrl = window.location.search.includes('rg=skip');
    const isBotDetected = isBot();
    const isEditorActive = isThemeEditorActive();
 

    if (!hasSavedRegion && !isDismissed && !isSkipUrl && !isBotDetected && !isEditorActive) { 
      setTimeout(() => {
        RegionGateAPI.open();
      }, 150);
    }  

    // 8. PDP Availability & Add-to-Cart Lock Logic
    let pdpObserver = null;

    function updatePDPAvailabilityPill(block, allowedStates) {
      const savedRegion = RegionGateAPI.get();
      const statusPill = block.querySelector('.rg-status-pill');
      
      let availability = 'available';
      if (savedRegion) {
        const upperAllowed = allowedStates.map(s => typeof s === 'string' ? s.toUpperCase().trim() : '');
        if (upperAllowed.length > 0) {
          const isAllowed = upperAllowed.includes(savedRegion.code.toUpperCase().trim()) || 
                            upperAllowed.includes(savedRegion.label.toUpperCase().trim());
          availability = isAllowed ? 'available' : 'unavailable';
        }
      } else {
        availability = 'unknown';
      }

      if (statusPill) {
        statusPill.className = 'rg-status-pill';
        statusPill.classList.add(`rg-status-pill--${availability}`);

        // let html = '';
        // if (availability === 'unknown') {
        //   html = `
        //     <svg class="rg-svg-icon rg-svg-icon--warning" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        //       <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
        //       <line x1="12" y1="9" x2="12" y2="13"></line>
        //       <line x1="12" y1="17" x2="12.01" y2="17"></line>
        //     </svg>
        //     Select state to check availability
        //   `;
        // } else if (availability === 'available') {
        //   html = `
        //     <svg class="rg-svg-icon rg-svg-icon--available" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
        //       <polyline points="20 6 9 17 4 12"></polyline>
        //     </svg>
        //     Ships to ${savedRegion.label}.
        //   `;
        // } else {
        //   html = `
        //     <svg class="rg-svg-icon rg-svg-icon--unavailable" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
        //       <line x1="18" y1="6" x2="6" y2="18"></line>
        //       <line x1="6" y1="6" x2="18" y2="18"></line>
        //     </svg>
        //     Doesn't ship to ${savedRegion.label}.
        //   `;
        // }
        // statusPill.innerHTML = html;
      }

      block.dataset.allowedStates = JSON.stringify(allowedStates);

      const cartBtns = document.querySelectorAll('button[name="add"], input[type="submit"].add-to-cart, .add-to-cart-btn');
      
      // Clean previous observer
      if (pdpObserver) {
        pdpObserver.disconnect();
        pdpObserver = null;
      }

      let isRestricted = availability === 'unavailable' || availability === 'unknown';
      if (isRestricted) {
        cartBtns.forEach(btn => {
          btn.setAttribute('disabled', 'true');
          btn.disabled = true;
          if (!btn.dataset.originalText) {
            btn.dataset.originalText = (btn.textContent || btn.value || 'Add to Cart').trim();
          }
          const targetText = btn.dataset.originalText;
          if (btn.tagName === 'INPUT') {
            btn.value = targetText;
          } else {
            btn.textContent = targetText;
          }
        });

        pdpObserver = new MutationObserver(mutations => {
          pdpObserver.disconnect();
          mutations.forEach(mutation => {
            mutation.target.setAttribute('disabled', 'true');
            mutation.target.disabled = true;
            const targetText = mutation.target.dataset.originalText || 'Add to Cart';
            const currentText = (mutation.target.tagName === 'INPUT' ? mutation.target.value : mutation.target.textContent).trim();
            if (currentText !== targetText) {
              if (mutation.target.tagName === 'INPUT') {
                mutation.target.value = targetText;
              } else {
                mutation.target.textContent = targetText;
              }
            }
          });
          cartBtns.forEach(btn => {
            pdpObserver.observe(btn, { attributes: true, childList: true, characterData: true, subtree: true });
          });
        });

        cartBtns.forEach(btn => {
          pdpObserver.observe(btn, { attributes: true, childList: true, characterData: true, subtree: true });
        });
      } else {
        cartBtns.forEach(btn => {
          btn.removeAttribute('disabled');
          btn.disabled = false;
          if (btn.dataset.originalText) {
            if (btn.tagName === 'INPUT') {
              btn.value = btn.dataset.originalText;
            } else {
              btn.textContent = btn.dataset.originalText;
            }
          }
        });
      }
    }

    const pdpBlocks = document.querySelectorAll('[data-rg-availability-container][data-context="pdp"]');
    if (pdpBlocks.length > 0) {
      pdpBlocks.forEach(block => {
        const allowedStatesJson = block.dataset.allowedStates;
        let allowedStates = [];
        try {
          allowedStates = JSON.parse(allowedStatesJson) || [];
        } catch (e) {
          allowedStates = allowedStatesJson ? allowedStatesJson.split(',').map(s => s.trim()) : [];
        }
        updatePDPAvailabilityPill(block, allowedStates);
      });

      // Wrap history state methods to detect variant updates via URL params
      const originalPushState = history.pushState;
      const originalReplaceState = history.replaceState;
      history.pushState = function() {
        originalPushState.apply(this, arguments);
        window.dispatchEvent(new Event('variant-changed'));
      };
      history.replaceState = function() {
        originalReplaceState.apply(this, arguments);
        window.dispatchEvent(new Event('variant-changed'));
      };
      window.addEventListener('popstate', () => {
        window.dispatchEvent(new Event('variant-changed'));
      });

      // Listen to variant changes (URL state updates or select change)
      window.addEventListener('variant-changed', () => {
        pdpBlocks.forEach(block => {
          const scriptEl = block.querySelector('[data-rg-variant-states]');
          if (scriptEl) {
            try {
              const variantStates = JSON.parse(scriptEl.textContent);
              const urlParams = new URLSearchParams(window.location.search);
              let variantId = urlParams.get('variant');
              if (!variantId) {
                const inputEl = document.querySelector('input[name="id"]');
                if (inputEl) variantId = inputEl.value;
              }
              if (variantId && variantStates[variantId]) {
                updatePDPAvailabilityPill(block, variantStates[variantId]);
              }
            } catch (e) {
              console.warn('RegionGate: Failed to parse variant states JSON', e);
            }
          }
        });
      });

      document.addEventListener('change', (e) => {
        if (e.target.closest('variant-selects, variant-radios, select[name="id"]')) {
          setTimeout(() => {
            window.dispatchEvent(new Event('variant-changed'));
          }, 50);
        }
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRegionGate);
  } else {
    initRegionGate();
  }





  // usman akram code 
  setTimeout(()=>{
    let headerMenuListItems = document.querySelectorAll('.rg-dropdown-li button') 
    headerMenuListItems.forEach(function(item) { 
      item.addEventListener('click', function(e) {
        let clickedState = e.target.getAttribute('data-rg-state-select')
        removeClasses('.rg-dropdown-li','selected')
        e.target.closest('.rg-dropdown-li').classList.add('selected')
        document.querySelectorAll('.rg-header-dropdown-list').forEach( dropdown => dropdown.style.display = 'none' )

        document.querySelectorAll('.rg-dropdown-search-input').forEach( searchInput => searchInput.value = item.textContent.trim() )
      });
    });

  },2000)
  

  function removeClasses(ele,cls){
    document.querySelectorAll(ele).forEach(item=>{
      item.classList.remove(cls)
    })
  }
 

})();




